import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { RoleBadge } from '../components/RoleBadge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { UserCircle, Award, TrendingUp, Target } from 'lucide-react';

export const Profile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.get('/profile');
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/users/${user.id}/password`, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setShowPasswordModal(false);
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!profile) {
    return <div className="flex items-center justify-center py-20">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#333333]">My Profile</h1>
        <Button
          onClick={() => setShowPasswordModal(true)}
          variant="outline"
          data-testid="change-password-button"
        >
          Change Password
        </Button>
      </div>

      {/* Profile Info */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          <div className="h-24 w-24 rounded-full bg-[#E31837]/10 flex items-center justify-center">
            <UserCircle className="h-16 w-16 text-[#E31837]" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-[#333333] mb-2">
              {profile.user.first_name} {profile.user.last_name}
            </h2>
            <div className="flex items-center gap-4 mb-4">
              <RoleBadge role={profile.user.role} />
              <span className="text-gray-600">{profile.user.email}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* All-Time Stats */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-[#333333]">All-Time Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-[#E31837]" />
              <span className="text-sm text-gray-600">Total Life Apps</span>
            </div>
            <p className="text-3xl font-bold text-[#333333]" data-testid="total-apps">
              {profile.all_time_stats.total_apps}
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-[#E31837]" />
              <span className="text-sm text-gray-600">Total Recruits</span>
            </div>
            <p className="text-3xl font-bold text-[#333333]" data-testid="total-recruits">
              {profile.all_time_stats.total_recruits}
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <Award className="h-5 w-5 text-[#E31837]" />
              <span className="text-sm text-gray-600">Total Base Premium</span>
            </div>
            <p className="text-3xl font-bold text-[#333333]" data-testid="total-bp">
              ${profile.all_time_stats.total_bp.toFixed(2)}
            </p>
          </Card>
        </div>
      </div>

      {/* Promotion History */}
      {profile.user.promotion_history && profile.user.promotion_history.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-[#333333]">Promotion History</h2>
          <Card className="p-6">
            <div className="space-y-4">
              {profile.user.promotion_history.map((entry, idx) => (
                <div
                  key={idx}
                  className="border-l-4 border-l-[#E31837] pl-4 py-2"
                  data-testid={`promotion-entry-${idx}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <RoleBadge role={entry.role} />
                    <span className="text-sm text-gray-500">
                      {new Date(entry.date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{entry.note}</p>
                  {entry.updated_by && (
                    <p className="text-xs text-gray-400 mt-1">Updated by: {entry.updated_by}</p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent data-testid="change-password-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E31837]">Change Password</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="old_password">Current Password *</Label>
              <Input
                id="old_password"
                type="password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                required
                data-testid="old-password-input"
              />
            </div>

            <div>
              <Label htmlFor="new_password">New Password *</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                data-testid="new-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirm_password">Confirm New Password *</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                data-testid="confirm-password-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordModal(false)}
                className="flex-1"
                data-testid="cancel-password-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                disabled={loading}
                data-testid="submit-password-button"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
